import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  /* ---------- Dark theme (default) ---------- */
  :root, [data-theme='dark'] {
    --bg: #070a14;
    --bg-alt: #0a0f1e;
    --surface: rgba(255, 255, 255, 0.03);
    --surface-hover: rgba(255, 255, 255, 0.06);
    --border: rgba(255, 255, 255, 0.08);
    --border-strong: rgba(255, 255, 255, 0.14);

    --text: #f1f5f9;
    --text-light: #f1f5f9;
    --text-muted: #94a3b8;
    --text-faint: #64748b;

    --primary: #6366f1;
    --primary-light: #818cf8;
    --violet: #a855f7;
    --secondary: #ec4899;
    --cyan: #22d3ee;

    --gradient-accent: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);
    --gradient-text: linear-gradient(120deg, #818cf8 0%, #c084fc 45%, #f472b6 100%);

    --nav-bg: rgba(7, 10, 20, 0.72);
    --elev-bg: rgba(12, 17, 32, 0.97);
    --mesh-op: 0.5;
    --wash1: rgba(99, 102, 241, 0.10);
    --wash2: rgba(236, 72, 153, 0.08);
    --spotlight: rgba(129, 140, 248, 0.14);
    --card-shadow: 0 22px 50px -28px rgba(0, 0, 0, 0.8);
    --rain-op: 0.5;
  }

  /* ---------- Light theme ---------- */
  [data-theme='light'] {
    --bg: #f5f6fb;
    --bg-alt: #ffffff;
    --surface: rgba(15, 23, 42, 0.025);
    --surface-hover: rgba(15, 23, 42, 0.05);
    --border: rgba(15, 23, 42, 0.10);
    --border-strong: rgba(15, 23, 42, 0.16);

    --text: #0f172a;
    --text-light: #0f172a;
    --text-muted: #475569;
    --text-faint: #94a3b8;

    --primary: #6366f1;
    --primary-light: #6d5ce6;
    --violet: #9333ea;
    --secondary: #db2777;
    --cyan: #0891b2;

    --gradient-accent: linear-gradient(135deg, #6366f1 0%, #9333ea 50%, #db2777 100%);
    --gradient-text: linear-gradient(120deg, #4f46e5 0%, #9333ea 45%, #db2777 100%);

    --nav-bg: rgba(255, 255, 255, 0.78);
    --elev-bg: rgba(255, 255, 255, 0.98);
    --mesh-op: 0.26;
    --wash1: rgba(99, 102, 241, 0.10);
    --wash2: rgba(236, 72, 153, 0.07);
    --spotlight: rgba(99, 102, 241, 0.10);
    --card-shadow: 0 22px 50px -30px rgba(99, 102, 241, 0.35);
    --rain-op: 0.28;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-body);
    line-height: 1.6;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-image:
      radial-gradient(900px 600px at 15% -5%, var(--wash1), transparent 60%),
      radial-gradient(800px 600px at 95% 5%, var(--wash2), transparent 55%);
    background-attachment: fixed;
    transition: background-color 0.4s ease, color 0.4s ease;
  }

  :root {
    --font-heading: 'Space Grotesk', 'Inter', sans-serif;
    --font-body: 'Inter', system-ui, sans-serif;
    --max-width: 1160px;
  }

  /* Wide / ultrawide screens: let the layout breathe instead of floating
     in a narrow strip. */
  @media (min-width: 1800px) {
    :root { --max-width: 1360px; }
  }
  @media (min-width: 2400px) {
    :root { --max-width: 1520px; }
    html { font-size: 17px; }
  }

  h1, h2, h3, h4 {
    font-family: var(--font-heading);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  a { color: inherit; text-decoration: none; }

  ::selection { background: rgba(168, 85, 247, 0.35); color: #fff; }

  ::-webkit-scrollbar { width: 10px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, var(--primary), var(--secondary));
    border-radius: 999px;
    border: 2px solid var(--bg);
  }

  .eyebrow {
    display: inline-block;
    font-family: var(--font-body);
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--primary-light);
    margin-bottom: 1rem;
  }

  .gradient-text {
    background: var(--gradient-text);
    background-size: 220% 220%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-shift 7s ease-in-out infinite;
  }

  @keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export default GlobalStyles;
